import { DEMO } from "src/app/config/brand.config";
import { CommonModule } from "@angular/common";
import { Component, inject, signal } from "@angular/core";
import { ActivatedRoute, Router, RouterModule } from "@angular/router";
import { SharedModule } from "src/shared/shared.module";
import { PipesModule } from "src/utils/pipes/pipes.module";

import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from "@angular/forms";
import { CartService } from "src/app/services/api/carts.service";
import { forkJoin, lastValueFrom, map, Observable } from "rxjs";
import { environment } from "src/environments/environment";
import { ToastService } from "src/app/services/toast-service/toast.service";
import { TransactionSummaryComponent } from "../components/transaction-summary/transaction-summary.component";
import { TranslateModule, TranslateService } from "@ngx-translate/core";
import { SkeletonOrderDetailComponent } from "./skeletons/skeleton-order-detail/skeleton-order-detail.component";

@Component({
  selector: "app-purchase-order-detail",
  imports: [
    CommonModule,
    ReactiveFormsModule,
    RouterModule,
    SharedModule,
    PipesModule,
    FormsModule,
    TransactionSummaryComponent,
    TranslateModule,
    SkeletonOrderDetailComponent,
  ],
  templateUrl: "./purchase-order-detail.component.html",
  styleUrl: "./purchase-order-detail.component.scss",
})
export class PurchaseOrderDetailComponent {
  readonly demo = DEMO;

  #route = inject(ActivatedRoute);
  #translate = inject(TranslateService);
  #fb = inject(FormBuilder);
  #cartService = inject(CartService);
  #toastService = inject(ToastService);

  transaction_id = signal<string | undefined>(undefined);

  transaction_info: any;
  bankAcc: any = [];
  form: FormGroup;

  isLoading = signal(true);
  /** The id in the URL names no order this account may read. */
  notFound = signal(false);

  fileUrl = environment.apiFileUrl;

  constructor() {
    this.#route.paramMap
      .pipe(map((p) => p.get("id") ?? ""))
      .subscribe((id) => this.transaction_id.set(id));
  }
  ngOnInit(): void {
    this.buildForm();
    this.fetchData();
  }

  buildForm() {
    this.form = this.#fb.group({
      bank_id: [null, Validators.required],
      transaction_id: this.transaction_id(),
      image: [null, Validators.required],
    });
  }

  _setLoading(value: boolean, time: number = 500) {
    setTimeout(() => {
      this.isLoading.update((_) => value);
    }, time);
  }

  async fetchData() {
    this._setLoading(true, 0);
    forkJoin([
      this.#cartService.getTransaction(
        +this.transaction_id()!
      ) as Observable<any>,
      this.#cartService.getBankAcc() as Observable<any>,
    ]).subscribe({
      next: ([transaction_info, bank_accounts]) => {
        // The API answers 200 with a null body for an id that names no order of
        // yours — `set_transaction_by_user` scopes by user and takes `.first`.
        // So the `error` branch below never runs on a bad id, and without this
        // the page rendered its heading, the bank notice and an empty
        // "หมายเลข Tracking : —" over nothing at all.
        if (!transaction_info) {
          this.notFound.set(true);
          this._setLoading(false);
          return;
        }

        if (transaction_info.discount_code) {
          transaction_info.discount =
            transaction_info?.discount -
            transaction_info?.discount_from_discount_code;
        }
        this.transaction_info = transaction_info;
        this.bankAcc = bank_accounts;

        // this.course_review = course_review;
      },
      // Without this, an id that names no order of yours rendered the heading,
      // the bank notice and an empty "หมายเลข Tracking : —" — a page that looks
      // like a working order with nothing behind it.
      error: () => {
        this.notFound.set(true);
        this._setLoading(false);
      },
      complete: () => {
        this._setLoading(false);
        // this.tableLoading.update((_) => false);
      },
    });
  }

  async submitPayment() {
    const payload = this.form.getRawValue();
    try {
      const submit = await lastValueFrom(
        this.#cartService.verifyTransaction(payload)
      );
      this.#toastService.toastSuccess(
        this.#translate.instant("APP.SUBMIT_PAYMENT_DONE")
      );
      this.fetchData();
    } catch (error) {
      this.#toastService.toastError(
        `❌ ${this.#translate.instant("VALIDATOR.SOMETHING_ERROR")}: ` + error
      );
    }
  }
  async cancelOrder() {
    try {
      const result = await lastValueFrom(
        this.#cartService.cancelTransaction(+this.transaction_id()!)
      );
      this.#toastService.toastSuccess(
        this.#translate.instant("APP.CANCEL_PAYMENT_DONE")
      );
      this.fetchData();
    } catch (error) {
      this.#toastService.toastError(
        `❌ ${this.#translate.instant("VALIDATOR.SOMETHING_ERROR")}: ` + error
      );
    }
  }
}
